import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { address, bankAccount, invitation, member, organization, team, teamMember, user } from './schema'

// ---- School class (grade level) ----
// Stored on user.school_class as text. Keep in sync with SCHOOL_GRADE_OPTIONS in @helper/utils/domain.
export const SCHOOL_CLASS_VALUES = [
  'kelas_0',
  'kelas_1',
  'kelas_2',
  'kelas_3',
  'kelas_4',
  'kelas_5',
  'kelas_6',
] as const
export const SchoolClassSchema = z.enum(SCHOOL_CLASS_VALUES)
export type SchoolClass = (typeof SCHOOL_CLASS_VALUES)[number]

// ---- Address ----
export const InsertAddressSchema = createInsertSchema(address, {
  id: z.string(),
  addressLineOne: z.string(),
  addressLineTwo: z.string().nullable(),
  village: z.string().nullable(),
  district: z.string().nullable(),
  regency: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
}).omit({
  createdAt: true,
  updatedAt: true,
})

export const UpdateAddressSchema = createUpdateSchema(address, {
  addressLineOne: z.string(),
  addressLineTwo: z.string().nullable(),
  village: z.string().nullable(),
  district: z.string().nullable(),
  regency: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
})
  .omit({ id: true, createdAt: true, updatedAt: true })
  .partial()

export const SelectAddressSchema = createSelectSchema(address, {
  id: z.string(),
  addressLineOne: z.string(),
  addressLineTwo: z.string().nullable(),
  village: z.string().nullable(),
  district: z.string().nullable(),
  regency: z.string().nullable(),
  province: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertAddress = typeof address.$inferInsert
export type UpdateAddress = z.infer<typeof UpdateAddressSchema>
export type SelectAddress = typeof address.$inferSelect

// ---- BankAccount ----
export const InsertBankAccountSchema = createInsertSchema(bankAccount, {
  id: z.string(),
  bankName: z.string(),
  accountNumber: z.string(),
  accountHolderName: z.string(),
  addressId: z.string().nullable(),
  swiftCode: z.string().nullable(),
  bankBranch: z.string().nullable(),
}).omit({
  createdAt: true,
  updatedAt: true,
})

export const UpdateBankAccountSchema = createUpdateSchema(bankAccount, {
  bankName: z.string(),
  accountNumber: z.string(),
  accountHolderName: z.string(),
  addressId: z.string().nullable(),
  swiftCode: z.string().nullable(),
  bankBranch: z.string().nullable(),
})
  .omit({ id: true, createdAt: true, updatedAt: true })
  .partial()

export const SelectBankAccountSchema = createSelectSchema(bankAccount, {
  id: z.string(),
  bankName: z.string(),
  accountNumber: z.string(),
  accountHolderName: z.string(),
  addressId: z.string().nullable(),
  swiftCode: z.string().nullable(),
  bankBranch: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertBankAccount = typeof bankAccount.$inferInsert
export type UpdateBankAccount = z.infer<typeof UpdateBankAccountSchema>
export type SelectBankAccount = typeof bankAccount.$inferSelect

// ---- User ----
export const InsertUserSchema = createInsertSchema(user, {
  id: z.string(),
  name: z.string(),
  email: z.email(),
  emailVerified: z.boolean().default(false),
  image: z.string().nullable(),
  role: z.string().nullable(),
  banned: z.boolean().default(false),
  banReason: z.string().nullable(),
  banExpires: z.date().nullable(),
  phoneNumber: z.string().nullable(),
  phoneNumberVerified: z.boolean().nullable(),
  userType: z.enum(['child', 'main']).default('main'),
  pin: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.date().nullable(),
  nickname: z.string().nullable(),
  shouldUpdateOnExternalServices: z.boolean().default(true),
  schoolClass: SchoolClassSchema.nullable(),
}).omit({
  createdAt: true,
  updatedAt: true,
})

export const UpdateUserSchema = createUpdateSchema(user, {
  name: z.string(),
  email: z.email(),
  emailVerified: z.boolean(),
  image: z.string().nullable(),
  role: z.string().nullable(),
  banned: z.boolean(),
  banReason: z.string().nullable(),
  banExpires: z.date().nullable(),
  phoneNumber: z.string().nullable(),
  phoneNumberVerified: z.boolean().nullable(),
  userType: z.enum(['child', 'main']),
  pin: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.date().nullable(),
  nickname: z.string().nullable(),
  shouldUpdateOnExternalServices: z.boolean(),
  schoolClass: SchoolClassSchema.nullable(),
})
  .omit({ id: true, createdAt: true, updatedAt: true })
  .partial()

export const SelectUserSchema = createSelectSchema(user, {
  id: z.string(),
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  role: z.string().nullable(),
  banned: z.boolean(),
  banReason: z.string().nullable(),
  banExpires: z.date().nullable(),
  phoneNumber: z.string().nullable(),
  phoneNumberVerified: z.boolean().nullable(),
  userType: z.enum(['child', 'main']),
  pin: z.string().nullable(),
  gender: z.string().nullable(),
  birthDate: z.date().nullable(),
  nickname: z.string().nullable(),
  shouldUpdateOnExternalServices: z.boolean(),
  schoolClass: SchoolClassSchema.nullable(),
})

export type InsertUser = typeof user.$inferInsert
export type UpdateUser = z.infer<typeof UpdateUserSchema>
export type SelectUser = typeof user.$inferSelect

// ---- Organization ----
export const InsertOrganizationSchema = createInsertSchema(organization, {
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  logo: z.string().nullable(),
  metadata: z.string().nullable(),
  type: z.array(z.string()),
  entityName: z.string().nullable(),
  settingsPin: z.string().nullable(),
  pin: z.string().nullable(),
  addressId: z.string().nullable(),
  bankAccountId: z.string().nullable(),
  npsn: z.string().nullable(),
  level: z.string().nullable(),
  shouldUpdateOnExternalServices: z.boolean().default(true),
}).omit({
  createdAt: true,
})

export const UpdateOrganizationSchema = createUpdateSchema(organization, {
  name: z.string(),
  slug: z.string(),
  logo: z.string().nullable(),
  metadata: z.string().nullable(),
  type: z.array(z.string()),
  entityName: z.string().nullable(),
  settingsPin: z.string().nullable(),
  pin: z.string().nullable(),
  addressId: z.string().nullable(),
  bankAccountId: z.string().nullable(),
  npsn: z.string().nullable(),
  level: z.string().nullable(),
  shouldUpdateOnExternalServices: z.boolean(),
})
  .omit({ id: true, createdAt: true })
  .partial()

export const SelectOrganizationSchema = createSelectSchema(organization, {
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  logo: z.string().nullable(),
  createdAt: z.date(),
  metadata: z.string().nullable(),
  type: z.array(z.string()),
  entityName: z.string().nullable(),
  settingsPin: z.string().nullable(),
  pin: z.string().nullable(),
  addressId: z.string().nullable(),
  bankAccountId: z.string().nullable(),
  npsn: z.string().nullable(),
  level: z.string().nullable(),
  shouldUpdateOnExternalServices: z.boolean(),
})

export type InsertOrganization = typeof organization.$inferInsert
export type UpdateOrganization = z.infer<typeof UpdateOrganizationSchema>
export type SelectOrganization = typeof organization.$inferSelect

// ---- Team ----
export const InsertTeamSchema = createInsertSchema(team, {
  id: z.string(),
  name: z.string(),
  organizationId: z.string(),
  image: z.string().nullable(),
}).omit({
  createdAt: true,
  updatedAt: true,
})

export const UpdateTeamSchema = createUpdateSchema(team, {
  name: z.string(),
  image: z.string().nullable(),
})
  .omit({ id: true, organizationId: true, createdAt: true, updatedAt: true })
  .partial()

export const SelectTeamSchema = createSelectSchema(team, {
  id: z.string(),
  name: z.string(),
  organizationId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
  image: z.string().nullable(),
})

export type InsertTeam = typeof team.$inferInsert
export type UpdateTeam = z.infer<typeof UpdateTeamSchema>
export type SelectTeam = typeof team.$inferSelect

// ---- Member ----
export const InsertMemberSchema = createInsertSchema(member, {
  id: z.string(),
  organizationId: z.string(),
  userId: z.string(),
  role: z.string().default('member'),
}).omit({
  createdAt: true,
})

export const UpdateMemberSchema = createUpdateSchema(member, {
  role: z.string(),
})
  .omit({ id: true, organizationId: true, userId: true, createdAt: true })
  .partial()

export const SelectMemberSchema = createSelectSchema(member, {
  id: z.string(),
  organizationId: z.string(),
  userId: z.string(),
  role: z.string(),
  createdAt: z.date(),
})

export type InsertMember = typeof member.$inferInsert
export type UpdateMember = z.infer<typeof UpdateMemberSchema>
export type SelectMember = typeof member.$inferSelect

// ---- TeamMember ----
export const InsertTeamMemberSchema = createInsertSchema(teamMember, {
  id: z.string(),
  teamId: z.string(),
  userId: z.string(),
  createdAt: z.date().nullable(),
})

export const UpdateTeamMemberSchema = createUpdateSchema(teamMember, {
  createdAt: z.date().nullable(),
})
  .omit({ id: true, teamId: true, userId: true, createdAt: true })
  .partial()

export const SelectTeamMemberSchema = createSelectSchema(teamMember, {
  id: z.string(),
  teamId: z.string(),
  userId: z.string(),
  createdAt: z.date().nullable(),
})

export type InsertTeamMember = typeof teamMember.$inferInsert
export type UpdateTeamMember = z.infer<typeof UpdateTeamMemberSchema>
export type SelectTeamMember = typeof teamMember.$inferSelect

// ---- Invitation ----
export const InsertInvitationSchema = createInsertSchema(invitation, {
  id: z.string(),
  organizationId: z.string(),
  email: z.email(),
  role: z.string().nullable(),
  teamId: z.string().nullable(),
  status: z.string().default('pending'),
  expiresAt: z.date(),
  inviterId: z.string(),
}).omit({
  createdAt: true,
})

export const UpdateInvitationSchema = createUpdateSchema(invitation, {
  role: z.string().nullable(),
  teamId: z.string().nullable(),
  status: z.string(),
})
  .omit({
    id: true,
    organizationId: true,
    email: true,
    inviterId: true,
    expiresAt: true,
    createdAt: true,
  })
  .partial()

export const SelectInvitationSchema = createSelectSchema(invitation, {
  id: z.string(),
  organizationId: z.string(),
  email: z.string(),
  role: z.string().nullable(),
  teamId: z.string().nullable(),
  status: z.string(),
  expiresAt: z.date(),
  createdAt: z.date(),
  inviterId: z.string(),
})

export type InsertInvitation = typeof invitation.$inferInsert
export type UpdateInvitation = z.infer<typeof UpdateInvitationSchema>
export type SelectInvitation = typeof invitation.$inferSelect
